import * as React from 'react'
import type { DateRange } from 'react-day-picker'
import { useTranslation } from 'react-i18next'

import { useEscapeStack } from '#/shared/lib/hooks'
import { DateRangePicker } from '#/shared/ui/date-picker'
import { MultiSelect } from '#/shared/ui/multi-select'
import type { MultiSelectOption } from '#/shared/ui/multi-select'
import { useShipmentFilterOptions } from '../lib/use-shipment-filter-options'
import {
  SHIPMENT_FILTER_KEYS,
  SHIPMENT_MULTI_FILTER_KEYS,
  getDateFilterConfig,
  getMultiFilterConfig,
  getStageDateConfig,
  isMultiFilterKey,
} from '../lib/use-shipment-list-filters'
import type {
  ShipmentFilterKey,
  ShipmentListFiltersState,
  ShipmentMultiFilterKey,
} from '../lib/use-shipment-list-filters'

/** Links the toggle's `aria-controls` to the panel region. */
export const SHIPMENT_FILTERS_PANEL_ID = 'shipment-filters-panel'

// Always-controlled stand-in for "no range": handing `undefined` to the
// picker would flip it into uncontrolled mode and freeze the cleared value.
const EMPTY_RANGE: DateRange = { from: undefined, to: undefined }

/**
 * The panel's `ShipmentFiltersButton` trigger, looked up within the same
 * toolbar (the panel's parent) so two mounted lists can never cross-bind.
 */
function findTrigger(panel: HTMLElement | null): HTMLElement | null {
  const scope: ParentNode = panel?.parentElement ?? document
  return scope.querySelector<HTMLElement>('[data-shipment-filters-trigger]')
}

function DateFilterField({
  state,
  filterKey,
}: {
  state: ShipmentListFiltersState
  filterKey: ShipmentFilterKey
}) {
  const { t } = useTranslation()
  const labelKey =
    filterKey === SHIPMENT_FILTER_KEYS.STAGE_DATE
      ? getStageDateConfig(state.stage).labelI18nKey
      : getDateFilterConfig(filterKey).labelI18nKey
  return (
    <DateRangePicker
      label={t(labelKey)}
      value={state.ranges[filterKey] ?? EMPTY_RANGE}
      onValueChange={(range) => state.setRange(filterKey, range)}
    />
  )
}

/**
 * Multi-select field that commits on dropdown close, not per checkbox — each
 * commit is a URL write and a table refetch, and toggling five states must
 * not thrash a low-end machine five times. Changes made with the dropdown
 * closed (tag ✕, Backspace) commit immediately.
 */
function MultiFilterField({
  state,
  filterKey,
  options,
  loading,
}: {
  state: ShipmentListFiltersState
  filterKey: ShipmentMultiFilterKey
  options: MultiSelectOption[]
  loading?: boolean
}) {
  const { t } = useTranslation()
  const [draft, setDraft] = React.useState<string[] | null>(null)
  const openRef = React.useRef(false)

  const commit = (values: string[]) =>
    state.setMulti(filterKey, values.length > 0 ? values : undefined)

  const handleValueChange = (values: string[]) => {
    if (openRef.current) setDraft(values)
    else commit(values)
  }

  const handleOpenChange = (open: boolean) => {
    openRef.current = open
    if (!open && draft !== null) {
      commit(draft)
      setDraft(null)
    }
  }

  return (
    <MultiSelect
      label={t(getMultiFilterConfig(filterKey).labelI18nKey)}
      options={options}
      value={draft ?? state.multi[filterKey] ?? []}
      onValueChange={handleValueChange}
      onOpenChange={handleOpenChange}
      placeholder={t(`toolbar.filterPlaceholders.${filterKey}`)}
      loading={loading}
    />
  )
}

interface ShipmentFiltersPanelProps {
  state: ShipmentListFiltersState
  /** True while the list is (re)fetching — shows the "Updating…" hint. */
  isFetching?: boolean
  /**
   * Closes the panel while true. Pages pass the mass-selection state: row
   * selection swaps the toolbar to mass actions and hides the Filters
   * toggle, so an open panel would have no remaining close control.
   */
  forceClose?: boolean
}

/**
 * Horizontal center of the `ShipmentFiltersButton` trigger relative to the
 * panel's left edge, so the pointer arrow sits under the button. Re-measured
 * on window resize and whenever the panel itself resizes — the phone rail
 * pushes (not overlays) content, which moves the trigger without a window
 * resize event.
 */
function useTriggerArrowLeft(
  isOpen: boolean,
  panelRef: React.RefObject<HTMLDivElement | null>,
): number | null {
  const [left, setLeft] = React.useState<number | null>(null)
  React.useLayoutEffect(() => {
    if (!isOpen) return
    const panel = panelRef.current
    if (!panel) return
    const measure = () => {
      const trigger = findTrigger(panel)
      if (!trigger) {
        setLeft(null)
        return
      }
      const panelRect = panel.getBoundingClientRect()
      const triggerRect = trigger.getBoundingClientRect()
      setLeft(triggerRect.left + triggerRect.width / 2 - panelRect.left)
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(panel)
    window.addEventListener('resize', measure)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [isOpen, panelRef])
  return left
}

/**
 * Panel open/close behaviour: focus moves into the region when it opens,
 * Escape closes it (layered under any open picker via the shared escape
 * stack) and hands focus back to the toggle, and `forceClose` shuts it when
 * the toolbar swaps to mass actions.
 */
function usePanelDismissal(
  state: ShipmentListFiltersState,
  panelRef: React.RefObject<HTMLDivElement | null>,
  forceClose: boolean,
) {
  const { isOpen, setOpen } = state

  const escapeClose = React.useCallback(() => {
    const trigger = findTrigger(panelRef.current)
    setOpen(false)
    trigger?.focus()
  }, [setOpen, panelRef])
  useEscapeStack(isOpen, escapeClose)

  React.useEffect(() => {
    if (forceClose && isOpen) setOpen(false)
  }, [forceClose, isOpen, setOpen])

  React.useEffect(() => {
    if (isOpen) panelRef.current?.focus()
  }, [isOpen, panelRef])
}

function UpdatingHint() {
  const { t } = useTranslation()
  return (
    <span
      aria-live="polite"
      className="absolute top-2.5 right-2.5 flex animate-in items-center gap-1.25 text-xs text-gray-500 fade-in duration-200"
    >
      <span
        aria-hidden
        className="size-1.5 animate-pulse rounded-full bg-primary"
      />
      {t('toolbar.updatingResults')}
    </span>
  )
}

function PanelArrow({ left }: { left: number | null }) {
  if (left == null) return null
  return (
    <span
      aria-hidden
      className="absolute -top-1.25 size-2.5 -translate-x-1/2 rotate-45 border-t border-l border-gray-200 bg-white"
      style={{ left }}
    />
  )
}

/**
 * Inline filters panel for the shipment list pages — slides down between the
 * toolbar and the table while the `ShipmentFiltersButton` toggle is open, and
 * lays every filter input out on an equal-width grid. Changes apply
 * immediately; the chip row above the table stays the applied-state summary.
 */
export function ShipmentFiltersPanel({
  state,
  isFetching = false,
  forceClose = false,
}: ShipmentFiltersPanelProps) {
  const { t } = useTranslation()
  const panelRef = React.useRef<HTMLDivElement | null>(null)
  const arrowLeft = useTriggerArrowLeft(state.isOpen, panelRef)
  usePanelDismissal(state, panelRef, forceClose)
  const hasSourcesApplied = Boolean(
    state.multi[SHIPMENT_MULTI_FILTER_KEYS.SOURCES]?.length,
  )
  // Shares its query key with the chip row's copy, so TanStack Query dedupes
  // this into the same single providers request.
  const options = useShipmentFilterOptions(state.isOpen || hasSourcesApplied)
  if (!state.isOpen) return null

  const optionsByKey: Record<ShipmentMultiFilterKey, MultiSelectOption[]> = {
    [SHIPMENT_MULTI_FILTER_KEYS.TIME_ZONE]: options.timezones,
    [SHIPMENT_MULTI_FILTER_KEYS.PICKUP_STATE]: options.states,
    [SHIPMENT_MULTI_FILTER_KEYS.DELIVERY_STATE]: options.states,
    [SHIPMENT_MULTI_FILTER_KEYS.SOURCES]: options.sources,
  }

  return (
    <div
      ref={panelRef}
      id={SHIPMENT_FILTERS_PANEL_ID}
      role="region"
      aria-label={t('toolbar.filters')}
      tabIndex={-1}
      className="relative grid animate-slide-down grid-cols-1 items-start gap-2.5 rounded-md border border-gray-200 bg-white p-2.5 focus:outline-none sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
    >
      <PanelArrow left={arrowLeft} />
      {isFetching && <UpdatingHint />}
      {state.groups
        .flat()
        .map((key) =>
          isMultiFilterKey(key) ? (
            <MultiFilterField
              key={key}
              state={state}
              filterKey={key}
              options={optionsByKey[key]}
              loading={
                key === SHIPMENT_MULTI_FILTER_KEYS.SOURCES &&
                options.sourcesLoading
              }
            />
          ) : (
            <DateFilterField key={key} state={state} filterKey={key} />
          ),
        )}
    </div>
  )
}
